import React, { ReactElement, ReactNode, useState } from "react";
import { useSearchParams } from "react-router";

interface TabProps {
  label: ReactNode;
  value: string;
  children: ReactNode;
}

// This is a "marker" component; it doesn't render itself,
// the parent Tabs component extracts its props.
export const Tab = ({ children }: TabProps) => <>{children}</>;

interface TabsContainerProps {
  children: ReactElement<TabProps> | ReactElement<TabProps>[];
  defaultValue?: string;
  className?: string;
  rightSlot?: (activeTab: string) => ReactNode;
  // When set, the active tab is stored in the URL under this query param,
  // making tabs deep-linkable.
  paramKey?: string;
}

export const Tabs = ({
  children,
  defaultValue,
  className,
  rightSlot,
  paramKey,
}: TabsContainerProps) => {
  const tabs = React.Children.toArray(children) as ReactElement<TabProps>[];
  const fallback = defaultValue || tabs[0]?.props.value;
  const validValues = tabs.map((t) => t.props.value);

  const [searchParams, setSearchParams] = useSearchParams();
  const [internalActive, setInternalActive] = useState(fallback);

  let activeTab: string;
  if (paramKey) {
    const fromUrl = searchParams.get(paramKey);
    activeTab = fromUrl && validValues.includes(fromUrl) ? fromUrl : fallback;
  } else {
    activeTab = internalActive;
  }

  const setActiveTab = (value: string) => {
    if (paramKey) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === fallback) {
            next.delete(paramKey);
          } else {
            next.set(paramKey, value);
          }
          return next;
        },
        { replace: true },
      );
    } else {
      setInternalActive(value);
    }
  };

  return (
    <div className={className}>
      <div className="mb-4 border-b border-gray-200">
        <div className="-mb-px flex items-center justify-between gap-3">
          <nav className="flex space-x-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const { label, value } = tab.props;
              const isActive = activeTab === value;

              return (
                <button
                  key={value}
                  onClick={() => setActiveTab(value)}
                  className={`${
                    isActive
                      ? "border-soluud-primary text-soluud-text-primary"
                      : "text-soluud-text-secondary hover:border-soluud-text-tertiary hover:text-soluud-text-primary border-transparent"
                  } border-b-[3px] px-1 py-4 text-sm font-medium whitespace-nowrap transition-colors duration-150`}
                >
                  {label}
                </button>
              );
            })}
          </nav>
          {/* Desktop: sort select inline with tab bar */}
          {rightSlot && (
            <div className="hidden shrink-0 pb-1 sm:block">
              {rightSlot(activeTab)}
            </div>
          )}
        </div>
      </div>
      {/* Mobile: sort select below tab bar */}
      {rightSlot && (
        <div className="mb-3 flex justify-end sm:hidden">
          {rightSlot(activeTab)}
        </div>
      )}

      {/* Render only the children of the currently active tab */}
      <div className="tab-content">
        {tabs.find((tab) => tab.props.value === activeTab)?.props.children}
      </div>
    </div>
  );
};

// Attach Tab to Tabs for the <Tabs.Tab> syntax
Tabs.Tab = Tab;
